<template>
  <span
    class="zero-edge-badge"
    :class="[`zero-edge-badge--${size}`, { 'zero-edge-badge--compact': compact }]"
    :aria-label="ariaLabel || t('BADGES.zeroEdge.ariaLabel')"
    role="note"
  >
    <i class="fa-solid fa-bolt" aria-hidden="true"></i>
    <span class="zero-edge-badge__label">{{ label || t('BADGES.zeroEdge.label') }}</span>
  </span>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

withDefaults(
  defineProps<{
    label?: string
    ariaLabel?: string
    size?: 'sm' | 'md'
    compact?: boolean
  }>(),
  {
    size: 'sm',
    compact: false,
  },
)

const { t } = useI18n()
</script>

<style scoped lang="scss">
.zero-edge-badge {
  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  white-space: nowrap;
  border-radius: 999px;
  border: 1px solid rgba(117, 255, 176, 0.58);
  background:
    linear-gradient(135deg, rgba(35, 255, 147, 0.2), rgba(255, 190, 85, 0.18)),
    rgba(9, 31, 29, 0.92);
  color: #baffcb;
  font-weight: 950;
  letter-spacing: 0.07em;
  line-height: 1;
  text-transform: uppercase;
  box-shadow:
    0 0 0 1px rgba(255, 214, 128, 0.13) inset,
    0 0 12px rgba(55, 241, 133, 0.28),
    0 0 24px rgba(255, 190, 85, 0.13);
  overflow: hidden;
  pointer-events: none;
  animation: zeroEdgePulse 2.2s ease-in-out infinite;

  &::before {
    content: '';
    position: absolute;
    inset: -80% -30%;
    z-index: -1;
    background: linear-gradient(
      105deg,
      transparent 36%,
      rgba(255, 255, 255, 0.52) 48%,
      transparent 60%
    );
    transform: translateX(-70%) rotate(8deg);
    animation: zeroEdgeSweep 2.6s ease-in-out infinite;
  }

  i {
    color: #ffd580;
    filter: drop-shadow(0 0 5px rgba(255, 213, 128, 0.78));
  }

  &--sm {
    padding: 0.18rem 0.42rem;
    font-size: 0.55rem;

    i {
      font-size: 0.52rem;
    }
  }

  &--md {
    padding: 0.25rem 0.52rem;
    font-size: 0.64rem;

    i {
      font-size: 0.62rem;
    }
  }

  &--compact {
    padding-inline: 0.36rem;
    letter-spacing: 0.04em;

    i {
      display: none;
    }
  }
}

@keyframes zeroEdgePulse {
  0%,
  100% {
    border-color: rgba(117, 255, 176, 0.5);
    box-shadow:
      0 0 0 1px rgba(255, 214, 128, 0.12) inset,
      0 0 10px rgba(55, 241, 133, 0.22),
      0 0 20px rgba(255, 190, 85, 0.1);
  }

  50% {
    border-color: rgba(255, 213, 128, 0.82);
    box-shadow:
      0 0 0 1px rgba(255, 214, 128, 0.24) inset,
      0 0 16px rgba(55, 241, 133, 0.4),
      0 0 32px rgba(255, 190, 85, 0.2);
  }
}

@keyframes zeroEdgeSweep {
  0%,
  45% {
    transform: translateX(-70%) rotate(8deg);
  }

  85%,
  100% {
    transform: translateX(70%) rotate(8deg);
  }
}
</style>
